/** Quick start, keyboard shortcuts and searchable reference of all help topics. */
import { useState } from 'preact/hooks';
import { HELP } from '../help';
import { tooltipsEnabled } from '../Tooltip';
import { Toggle } from '../controls';

const SHORTCUTS: [string, string][] = [
  ['V · H · L · R · E · P · O · C', 'Select · Pan · Line · Rectangle · Ellipse · Polygon · Point · Calibrate'],
  ['Space (hold) / middle drag', 'Pan'],
  ['Wheel', 'Zoom at cursor'],
  ['F · 1 · + · −', 'Fit · 1:1 · Zoom in · Zoom out'],
  ['Shift while drawing', 'Constrain line angle (15°) / square / circle'],
  ['Alt while drawing', 'Rectangle / ellipse from centre'],
  ['⌘/Ctrl while drawing', 'Invert pixel-centre snapping'],
  ['S', 'Toggle snap to pixel centres'],
  ['Arrows (Shift ×10, Alt ×0.1)', 'Nudge selected ROI'],
  ['Delete / Backspace', 'Delete selected ROI (or last polygon vertex)'],
  ['Enter / Esc', 'Close polygon / cancel, deselect'],
  ['⌘Z · ⇧⌘Z', 'Undo · Redo ROI changes'],
  ['⌘D', 'Duplicate selected ROI'],
  ['⌘V (⇧⌘V)', 'Paste image into A (B)'],
  ['⌘O (⇧⌘O)', 'Open image into A (B)'],
  ['G (⇧G)', 'Freeze screen capture into A (B)'],
  ['I', 'Freeze pixel inspector'],
  ['M', 'Cycle view mode: colour → quantity → false colour'],
  ['⌘S · ⌘E', 'Save session · Export panel'],
  ['?', 'This help'],
];

export function HelpPanel() {
  const [q, setQ] = useState('');
  const entries = Object.entries(HELP).filter(
    ([k, h]) => !q || `${k} ${h.title} ${h.text} ${h.how ?? ''}`.toLowerCase().includes(q.toLowerCase()),
  );
  return (
    <div class="help-panel">
      <h3>Quick start</h3>
      <ol>
        <li>
          <b>Load</b> an image: drop a file, paste with <kbd>⌘V</kbd>, use File → Open, or start a live <b>Capture</b> and press{' '}
          <kbd>G</kbd> to freeze a frame.
        </li>
        <li>
          Check <b>Photometry</b>: the transfer function and primaries are detected automatically; choose the <b>measured quantity</b>{' '}
          (luminance Y by default).
        </li>
        <li>
          Press <kbd>L</kbd> and drag across the feature. Drag the round handles to widen the averaging band.
        </li>
        <li>
          Read the <b>Line profile</b> and <b>Metrics</b> (peak, FWHM, uniformity, edge width). Hover the plot to locate points on the
          image.
        </li>
        <li>
          Optional: <b>Calibrate</b> to cd/m² and mm, compare against image <b>B</b>, analyse <b>spectrum / MTF</b>, then <b>Export</b> CSV,
          charts and reports.
        </li>
      </ol>
      <Toggle label="Show hover help" checked={tooltipsEnabled.value} onChange={(v) => (tooltipsEnabled.value = v)} />
      <h3>Keyboard shortcuts</h3>
      <table class="kv shortcuts">
        <tbody>
          {SHORTCUTS.map(([k, v]) => (
            <tr key={k}>
              <th>
                <kbd>{k}</kbd>
              </th>
              <td>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h3>Measurement conventions</h3>
      <ul class="conventions">
        <li>Pixel (i, j) covers [i, i+1) × [j, j+1); its centre is (i + 0.5, j + 0.5) — the ImageJ convention.</li>
        <li>All sampling and averaging happen on linearised values; display settings never alter measurements.</li>
        <li>Profile samples outside the image are gaps (NaN) and are excluded from every metric.</li>
        <li>
          Screen captures measure encoded display intent (relative luminance), not emitted light. Absolute cd/m² requires a calibrated
          camera image of the physical display.
        </li>
      </ul>
      <h3>Reference</h3>
      <input
        type="search"
        class="help-search"
        placeholder="Search help…"
        value={q}
        onInput={(e) => setQ((e.target as HTMLInputElement).value)}
      />
      <dl class="help-list">
        {entries.map(([k, h]) => (
          <div key={k} class="help-item">
            <dt>
              {h.title}
              {h.keys && <kbd>{h.keys}</kbd>}
            </dt>
            <dd>
              {h.text}
              {h.how && <div class="muted">{h.how}</div>}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
